import { useCallback, useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, Paper } from '@mui/material';
import { useSearchParams } from 'react-router';

import TabPanel from '@/components/TabPanel';
import Tabs from '@/components/Tabs';
import { PageInformationContext } from '@/context/PageInformationContext';
import FactsheetWindow from '@/features/Factsheet/components/FactsheetView/FactsheetWindow';
import { FACTSHEET_TAB_PARAM_NAME } from '@/features/Factsheet/constants';
import updateTabNames from '@/features/Factsheet/utils/updateTabNames';
import { addStringIfNotExists } from '@/utils/addStringIfNotExists';
import filterOutElementAtIndex from '@/utils/filterOutElementAtIndex';
import splitCommaSeparatedKeys from '@/utils/splitCommaSeparatedKeys';

const FactsheetView = ({
  factsheetKeys,
  onDialogClose,
  setAdditionalTabsParam
}: {
  factsheetKeys: string;
  onDialogClose?: () => void;
  setAdditionalTabsParam?: (tabs: string[]) => void;
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [openedFactsheets, setOpenedFactsheets] = useState<string[]>([]);
  const [tabNames, setTabNames] = useState<Map<string, string>>(new Map());
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  useEffect(() => {
    const codes = splitCommaSeparatedKeys(factsheetKeys);
    setOpenedFactsheets(codes);
    setActiveTab(codes.length - 1);
    setTabNames(new Map(codes.map(code => [code, code])));
  }, [factsheetKeys]);

  const deleteActiveTabParam = useCallback(() => {
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.delete(FACTSHEET_TAB_PARAM_NAME);
    setUrlSearchParams(newSearchParams);
  }, [setUrlSearchParams, urlSearchParams]);

  const handleChangeTab = useCallback(
    (value: number) => {
      deleteActiveTabParam();
      setActiveTab(value);
    },
    [deleteActiveTabParam]
  );

  const onClose = useCallback(
    (indexToClose: number) => {
      const updated = filterOutElementAtIndex(openedFactsheets, indexToClose);
      setOpenedFactsheets(updated);

      if (setAdditionalTabsParam) {
        setAdditionalTabsParam(updated.slice(1));
      }

      if (indexToClose <= activeTab) {
        setActiveTab(prev => Math.max(prev - 1, 0));
      }
      deleteActiveTabParam();
    },

    [activeTab, deleteActiveTabParam, openedFactsheets, setAdditionalTabsParam]
  );

  const handleUpdateTabName = useCallback((code: string, newName: string) => {
    setTabNames(prev => updateTabNames(prev, code, newName));
  }, []);

  const handleOpenNewTab = useCallback(
    (code: string) => {
      const currentIndex = openedFactsheets.indexOf(code);

      if (currentIndex !== -1) {
        setActiveTab(currentIndex);
        return;
      }

      const updated = addStringIfNotExists(openedFactsheets, code);
      setOpenedFactsheets(updated);
      setActiveTab(updated.length - 1);

      if (setAdditionalTabsParam && updated.length > 1) {
        setAdditionalTabsParam(updated.slice(1));
      }

      setTabNames(prev => updateTabNames(prev, code, code));
    },
    [openedFactsheets, setAdditionalTabsParam]
  );
  const pageInformation = useMemo(() => {
    return {
      mainPageTitle: 'factsheets',
      subPageTitle: openedFactsheets?.[activeTab] ?? ''
    };
  }, [openedFactsheets, activeTab]);

  return (
    <PageInformationContext.Provider value={pageInformation}>
      <Paper
        sx={{
          width: 1024,
          maxWidth: '100%',
          mx: 8,
          my: 5,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            px: 3
          }}
        >
          <Tabs
            activeTab={activeTab}
            tabs={openedFactsheets.map((code, index) => ({
              name: tabNames.get(code) ?? code,
              closable: !!index,
              onClose
            }))}
            onChange={handleChangeTab}
          />
          {onDialogClose ? (
            <ButtonBase
              aria-label='close'
              sx={{ width: 24, height: 24 }}
              onClick={event => {
                event.stopPropagation();
                onDialogClose();
              }}
            >
              <CloseIcon />
            </ButtonBase>
          ) : (
            <Box
              component='a'
              display='flex'
              href='/'
            >
              <img
                alt='logoGx'
                src='/gx-logo-white.svg'
                style={{
                  width: 130,
                  height: 40
                }}
              />
            </Box>
          )}
        </Box>

        {openedFactsheets.map((code, index) => (
          <TabPanel
            key={code + index}
            containerSX={{
              flexGrow: 1,
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              ...(index === activeTab ? { display: 'flex' } : {})
            }}
            index={index}
            sx={{
              display: 'flex',
              flexGrow: 1,
              flex: 1,
              height: '100%',
              width: '100%'
            }}
            value={activeTab}
          >
            <FactsheetWindow
              factsheetKey={code}
              openNewTab={handleOpenNewTab}
              updateTabName={handleUpdateTabName}
            />
          </TabPanel>
        ))}
      </Paper>
    </PageInformationContext.Provider>
  );
};
export default FactsheetView;
